import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'
import { useAppDispatch } from '@/hooks/useRedux'
import { updateUser, setIsLoading } from '@/pages/user/userSlice'
import {
  validateWebSessionToken,
  setWebSessionToken,
  getProfileWithToken,
} from '@/services/webSession'

type FlowState = 'validating' | 'fetching_user' | 'redirecting' | 'error'

/**
 * Error fallback component for WebSessionPage
 * Shows user-friendly error with instructions to return to the app
 */
function WebSessionErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center max-w-md p-6">
        <div className="text-red-500 mb-4">
          <svg
            className="w-16 h-16 mx-auto"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h1 className="text-xl font-semibold text-gray-800 mb-2">
          Session Error
        </h1>
        <p className="text-gray-600 mb-6">
          {error.message || 'An unexpected error occurred.'}
        </p>
        <div className="space-y-3">
          <button
            onClick={resetErrorBoundary}
            className="w-full px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors"
          >
            Try Again
          </button>
          <p className="text-sm text-gray-500">
            Or close this window and try again from the DUPR app.
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * Web Session Page - Token Exchange Handler
 *
 * This page handles the web session token validation flow:
 * 1. Receives a single-use token from the mobile app via query param
 * 2. Validates the token with the backend, receiving an access token
 * 3. Stores the access token in sessionStorage
 * 4. Fetches the user profile
 * 5. Redirects to the target page (e.g., /redeem)
 *
 * URL Format:
 *   /web-session?token=XXX&redirect_b64=BASE64_ENCODED_REDIRECT_URL
 *
 * Example (mobile app should construct):
 *   const redirectUrl = '/redeem?promotionId=abc&clientKey=xyz'
 *   const encoded = btoa(redirectUrl)  // Base64 encode
 *   const url = `/web-session?token=${token}&redirect_b64=${encoded}`
 *
 * The session is fully isolated from the main cookie-based auth:
 * - Uses sessionStorage, not localStorage or cookies
 * - Token survives page refresh but not tab/browser close
 * - Does not affect existing logged-in users on the main website
 */
function WebSessionContent() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const [flowState, setFlowState] = useState<FlowState>('validating')
  const [errorMessage, setErrorMessage] = useState<string>('')

  const token = searchParams.get('token')

  // The redirect URL can be passed as:
  // 1. Base64 encoded (recommended): ?redirect_b64=L3JlZGVlbT9wcm9tb3Rpb25JZD1hYmM=
  // 2. URL encoded: ?redirect=%2Fredeem%3FpromotionId%3Dabc
  // Base64 is preferred to avoid issues with nested query parameter encoding
  const redirectB64 = searchParams.get('redirect_b64')
  const redirectParam = searchParams.get('redirect')

  let redirect = '/redeem'
  if (redirectB64) {
    try {
      redirect = atob(redirectB64)
    } catch (e) {
      console.error('Failed to decode redirect_b64:', e)
    }
  } else if (redirectParam) {
    redirect = redirectParam
  }

  useEffect(() => {
    const processWebSession = async () => {
      if (!token) {
        setFlowState('error')
        setErrorMessage('Missing session token')
        return
      }

      try {
        // Step 1: Validate the web session token
        setFlowState('validating')
        const validation = await validateWebSessionToken(token)

        // Step 2: Store the access token in sessionStorage
        setWebSessionToken(validation.accessToken, validation.expiresIn)

        // Step 3: Fetch user profile
        setFlowState('fetching_user')
        dispatch(setIsLoading(true))
        const userProfile = await getProfileWithToken(validation.accessToken)
        dispatch(updateUser(userProfile))
        dispatch(setIsLoading(false))

        // Step 4: Redirect to target page
        setFlowState('redirecting')
        navigate(redirect, { replace: true })
      } catch (error) {
        console.error('Web session validation failed:', error)
        setFlowState('error')
        setErrorMessage(
          error instanceof Error ? error.message : 'Failed to validate session. Please try again.'
        )
        dispatch(setIsLoading(false))
      }
    }

    processWebSession()
  }, [token, redirect, navigate, dispatch])

  // Render error state
  if (flowState === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-6">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-800 mb-2">Session Error</h1>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <p className="text-sm text-gray-500">Please close this window and try again from the app.</p>
        </div>
      </div>
    )
  }

  // Render loading/processing state
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
        <p className="text-gray-600">
          {flowState === 'validating' && 'Validating session...'}
          {flowState === 'fetching_user' && 'Loading your account...'}
          {flowState === 'redirecting' && 'Redirecting...'}
        </p>
      </div>
    </div>
  )
}

/**
 * Main export - WebSessionPage wrapped with ErrorBoundary
 * Catches any unexpected rendering errors and shows error UI instead of crashing
 */
export function WebSessionPage() {
  return (
    <ErrorBoundary FallbackComponent={WebSessionErrorFallback}>
      <WebSessionContent />
    </ErrorBoundary>
  )
}

export default WebSessionPage
